import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';
import { bootGrScrollEngine, GrScrollEngine } from '@theme/gr-scroll-engine';

class GrSelectionModule extends Component {
  
  #trigger = null;

  
  #parallaxTween = null;

  
  #openCover = null;

  
  #dualMediaMql = null;

  #nearObserver = null;

  #isNear = false;

  connectedCallback() {
    super.connectedCallback();

    this.#bindInteractions();
    this.#bindDualMedia();
    this.#bindNearViewport();

    if (!this.#shouldParallax()) return;

    window.addEventListener('gr-scroll:ready', this.#initParallax);
    bootGrScrollEngine().then((engine) => {
      if (engine) this.#initParallax();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('gr-scroll:ready', this.#initParallax);
    this.#dualMediaMql?.removeEventListener('change', this.#syncDualMedia);
    this.#nearObserver?.disconnect();
    this.#trigger?.kill();
    this.#parallaxTween?.kill();
    this.#closeOutfit();
  }

  #shouldParallax() {
    return (
      this.hasAttribute('data-enable-parallax') &&
      document.documentElement.hasAttribute('data-gr-scroll') &&
      !prefersReducedMotion()
    );
  }

  #bindInteractions() {
    if (this.hasAttribute('data-enable-hover-dim') && window.matchMedia('(hover: hover)').matches) {
      this.querySelectorAll('.gr-selection-module__cover:not(.is-static)').forEach((cover) => {
        cover.addEventListener('mouseenter', () => cover.classList.add('is-hovered'));
        cover.addEventListener('mouseleave', () => cover.classList.remove('is-hovered'));
      });
    }

    this.addEventListener('click', this.#handleClick);
    document.addEventListener('keydown', this.#handleKeydown);
  }

  #bindDualMedia() {
    this.#dualMediaMql = window.matchMedia('(max-width: 500px)');
    this.#syncDualMedia();
    this.#dualMediaMql.addEventListener('change', this.#syncDualMedia);
  }

  /** Videos render with preload="none" so they don't compete with the hero; start them a screen ahead. */
  #bindNearViewport() {
    if (!('IntersectionObserver' in window)) {
      this.#isNear = true;
      this.#syncDualMedia();
      return;
    }
    this.#nearObserver = new IntersectionObserver(
      (entries) => {
        this.#isNear = entries.some((entry) => entry.isIntersecting);
        this.#syncDualMedia();
      },
      { rootMargin: '100% 0px' }
    );
    this.#nearObserver.observe(this);
  }

  #syncDualMedia = () => {
    const isMobile = this.#dualMediaMql?.matches ?? false;
    this.querySelectorAll('video.gr-selection-module__video').forEach((el) => {
      if (!(el instanceof HTMLVideoElement)) return;
      const isDualMobile =
        el.classList.contains('gr-selection-module__video--mobile') &&
        Boolean(el.closest('.gr-selection-module__cover--dual'));

      if (this.#isNear && (!isDualMobile || isMobile)) {
        el.muted = true;
        el.playsInline = true;
        el.preload = 'auto';
        el.play().catch(() => {});
      } else if (!el.paused) {
        el.pause();
        if (isDualMobile && !isMobile) el.currentTime = 0;
      }
    });
  };

  
  #handleKeydown = (event) => {
    if (event.key === 'Escape') this.#closeOutfit();
  };

  
  #handleClick = (event) => {
    const trigger = event.target.closest('[data-selection-trigger]');
    const overlay = this.querySelector('[data-selection-overlay]');

    if (trigger) {
      event.preventDefault();
      const cover = trigger.closest('.gr-selection-module__cover');
      const source = cover?.querySelector('[data-selection-outfit]');
      if (!(cover instanceof HTMLElement) || !(source instanceof HTMLElement)) return;

      if (this.#openCover === cover) {
        this.#closeOutfit();
        return;
      }

      this.#openOutfit(cover, source);
      return;
    }

    if (event.target === overlay) {
      this.#closeOutfit();
    }
  };

  
  #openOutfit(cover, source) {
    const drawer = this.querySelector('[data-selection-drawer]');
    const track = this.querySelector('[data-selection-drawer-track]');
    const overlay = this.querySelector('[data-selection-overlay]');
    const sourceTrack = source.querySelector('.gr-selection-module__outfit-track');

    if (!(drawer instanceof HTMLElement) || !(track instanceof HTMLElement) || !(sourceTrack instanceof HTMLElement)) {
      return;
    }

    this.#closeOutfit();

    track.replaceChildren(...Array.from(sourceTrack.children).map((node) => node.cloneNode(true)));

    this.#openCover = cover;
    cover.classList.add('is-open');
    drawer.hidden = false;
    drawer.setAttribute('aria-hidden', 'false');

    requestAnimationFrame(() => drawer.classList.add('is-visible'));

    if (overlay instanceof HTMLElement) {
      overlay.hidden = false;
      requestAnimationFrame(() => overlay.classList.add('is-visible'));
    }

    document.documentElement.classList.add('gr-selection-outfit-open');
  }

  #closeOutfit() {
    const drawer = this.querySelector('[data-selection-drawer]');
    const track = this.querySelector('[data-selection-drawer-track]');
    const overlay = this.querySelector('[data-selection-overlay]');

    if (this.#openCover) {
      this.#openCover.classList.remove('is-open');
      this.#openCover = null;
    }

    if (drawer instanceof HTMLElement) {
      drawer.classList.remove('is-visible');
      const finish = () => {
        if (drawer.classList.contains('is-visible')) return;
        drawer.hidden = true;
        drawer.setAttribute('aria-hidden', 'true');
        if (track instanceof HTMLElement) track.replaceChildren();
      };
      drawer.addEventListener('transitionend', finish, { once: true });
      setTimeout(finish, 400);
    }

    if (overlay instanceof HTMLElement) {
      overlay.classList.remove('is-visible');
      const finishOverlay = () => {
        if (overlay.classList.contains('is-visible')) return;
        overlay.hidden = true;
      };
      overlay.addEventListener('transitionend', finishOverlay, { once: true });
      setTimeout(finishOverlay, 400);
    }

    document.documentElement.classList.remove('gr-selection-outfit-open');
  }

  #initParallax = () => {
    if (this.#trigger || !this.#shouldParallax()) return;

    const engine = GrScrollEngine.get();
    const gsap = engine?.gsap;
    const ScrollTrigger = engine?.ScrollTrigger;
    const items = this.querySelectorAll('[data-parallax-item]');

    if (!gsap || !ScrollTrigger || !items.length) return;

    const startValues = [];
    const endValues = [];

    items.forEach((item) => {
      startValues.push(Number.parseFloat(item.getAttribute('data-parallax-start') || '0'));
      endValues.push(Number.parseFloat(item.getAttribute('data-parallax-end') || '0'));
    });

    this.#parallaxTween = gsap.fromTo(
      items,
      {
        yPercent: (index) => startValues[index] ?? 0,
      },
      {
        yPercent: (index) => endValues[index] ?? 0,
        ease: 'none',
        scrollTrigger: {
          trigger: this,
          start: 'top bottom',
          end: 'bottom top',
          scrub: true,
        },
      }
    );

    this.#trigger =  (
      this.#parallaxTween.scrollTrigger
    );
  };
}

if (!customElements.get('gr-selection-module')) {
  customElements.define('gr-selection-module', GrSelectionModule);
}
